import React, { useState } from 'react';
import { Search, ShoppingBag, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n';

interface MobileOrderQueryProps {
  user?: any;
  isAuthenticated?: boolean;
  onOrderQuery?: (orderNumber: string) => void;
}

const MobileOrderQuery: React.FC<MobileOrderQueryProps> = ({
  user,
  isAuthenticated,
  onOrderQuery
}) => {
  const { t } = useI18n();
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [isSearchingOrder, setIsSearchingOrder] = useState<boolean>(false);
  const [querySuccess, setQuerySuccess] = useState<boolean>(false);

  // 订单查询处理函数
  const handleOrderQuery = async () => {
    if (!orderNumber.trim() || !user || isSearchingOrder || !onOrderQuery) return;
    
    try {
      setIsSearchingOrder(true);
      setQuerySuccess(false);
      
      await onOrderQuery(orderNumber.trim());
      
      // 查询成功，显示成功状态
      setQuerySuccess(true);
      setOrderNumber(''); // 清空输入框
      
      // 1.5秒后自动重置状态（用户能看到成功反馈后会自动跳转到文件视图）
      setTimeout(() => {
        setQuerySuccess(false);
      }, 1500);
      
    } catch (error) {
      console.error('订单查询失败:', error);
      setQuerySuccess(false);
    } finally {
      setIsSearchingOrder(false);
    }
  };

  // 回车键查询
  const handleOrderInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleOrderQuery();
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className="portal-glass-card max-w-sm w-full text-center space-y-6 animate-fade-in">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl flex items-center justify-center mx-auto shadow-lg shadow-orange-500/20">
            <ShoppingBag className="w-10 h-10 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('portal.orderQuery')}</h2>
            <p className="text-gray-500 dark:text-gray-400">请先登录以使用订单查询功能</p>
          </div>
          
          <div className="bg-blue-500/10 dark:bg-blue-500/20 rounded-2xl p-4 text-left">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-semibold mb-1">功能说明：</p>
                <p>输入订单号可将订单文件夹添加到您的账户，方便管理和访问相关文件。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full animate-fade-in">
      {/* 页面标题 - 移动端紧凑版 */}
      <div className="px-4 pt-6 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{t('portal.orderQuery')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">输入您的订单号以获取专属文件夹访问权限</p>
      </div>

      {/* 查询表单 - 移动端优化 */}
      <div className="px-4 pb-6">
        <div className="bg-white/90 dark:bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-gray-200/50 dark:border-white/10 shadow-sm space-y-5">
          <div className="space-y-3">
            <div>
              <label htmlFor="order-number" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('portal.orderNumber')}
              </label>
              <div className="relative group">
                <input
                  id="order-number"
                  type="text"
                  placeholder={t('portal.orderNumberInputPlaceholder')}
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  onKeyPress={handleOrderInputKeyPress}
                  className="w-full h-12 pl-4 pr-12 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/20 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none text-base"
                  disabled={isSearchingOrder}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1.5 rounded-lg bg-orange-500/10 text-orange-500">
                  <ShoppingBag className="w-5 h-5" />
                </div>
              </div>
            </div>

            <button
              onClick={handleOrderQuery}
              disabled={!orderNumber.trim() || isSearchingOrder || querySuccess}
              className={cn(
                'w-full h-12 px-6 text-base font-bold rounded-xl transition-all duration-300',
                !orderNumber.trim() || isSearchingOrder
                  ? 'bg-gray-100 dark:bg-white/5 text-gray-400 cursor-not-allowed'
                  : querySuccess
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                  : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/20 active:scale-[0.98]'
              )}
            >
              {isSearchingOrder ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  {t('portal.searching')}
                </div>
              ) : querySuccess ? (
                <div className="flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {t('portal.querySuccessRedirecting')}
                </div>
              ) : (
                t('portal.searchNow')
              )}
            </button>
          </div>
          
          <div className="pt-3 border-t border-gray-200/50 dark:border-white/5">
            <div className="flex items-start gap-2.5 text-xs text-gray-500 dark:text-gray-400">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <p>{t('portal.orderQueryHint', '输入文件夹对应的订单号，获取限时访问权限')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileOrderQuery;
